import { getPropertyById } from "../../../../SOA/api";
import { getSnowLogById } from "./Api";

/**
 * Default initial values for a new Snow/Ice Removal Log
 * Matches the current Mongoose schema
 */
export const defaultSnowLog = {
  get date() {
    return new Date().toLocaleDateString('en-CA');
  },
  time_started: '',
  time_completed: '',
  tasks: {
    sidewalks: false,
    all_entrances: false,
    stairs: false,
    ramp: false,
    amenity: false,
    others: false,
  },
  actions_performed: [],
  temp_and_conditions: '',
  attachments: [],
  comments: '',
  completed_work: null,
  property_details: {
    property_id: '',
    building_name: '',
  },
};

/**
 * Fetch property details when creating/editing a log
 * @param {string} propertyId - Property ID
 * @returns {Promise<Object>} Property info
 */
export const getPropertyDetailsForSnowLog = async (propertyId) => {
  try {
    if (!propertyId) {
      throw new Error('Property ID is required');
    }

    const property = await getPropertyById(propertyId);
    if (!property) {
      throw new Error('Property not found');
    }

    return { property };
  } catch (error) {
    console.error('Error fetching property details for snow log:', error);
    throw error;
  }
};

/**
 * Auto-fill property details when a property is  
 * @param {Object} currentFormValues - Current form values
 * @param {string} propertyId - Selected property ID
 * @param {string} [buildingName=''] - Optional building name
 * @returns {Promise<Object>} Updated form values
 */
export const autoFillPropertyDetails = async (
  currentFormValues,
  propertyId,
  buildingName = ''
) => {
  try {
    if (!propertyId) {
      return {
        ...currentFormValues,
        property_details: {
          property_id: '',
          building_name: '',
        },
        actions_performed: currentFormValues.actions_performed || [],
      };
    }

    const { property } = await getPropertyDetailsForSnowLog(propertyId);

    let buildingNameToUse = buildingName;
    if (!buildingNameToUse && property.building && Array.isArray(property.building)) {
      buildingNameToUse = property.building[0]?.buildingName || '';
    }

    return {
      ...currentFormValues,
      property_details: {
        property_id: propertyId,
        building_name: buildingNameToUse.trim(),
      },
      date: currentFormValues.date || new Date().toLocaleDateString('en-CA'),
      tasks: currentFormValues.tasks || defaultSnowLog.tasks,
      actions_performed: currentFormValues.actions_performed || [],
      attachments: currentFormValues.attachments || [],
      comments: currentFormValues.comments || '',
    };
  } catch (error) {
    console.error('Failed to auto-fill property details:', error);
    return currentFormValues;
  }
};

/**
 * Get initial values for editing an existing Snow/Ice Removal Log
 * @param {string} id - Snow Log ID
 * @returns {Promise<Object>} Formatted initial values for Formik
 */
export const getSnowLogInitialValues = async (id) => {
  if (!id) return defaultSnowLog;

  try {
    const log = await getSnowLogById(id);
    return {
      date: log.date ? new Date(log.date).toISOString().split('T')[0] : '',
      time_started: log.time_started || '',
      time_completed: log.time_completed || '',
      tasks: {
        sidewalks: !!log.tasks?.sidewalks,
        all_entrances: !!log.tasks?.all_entrances,
        stairs: !!log.tasks?.stairs,
        ramp: !!log.tasks?.ramp,
        amenity: !!log.tasks?.amenity,
        others: !!log.tasks?.others,
      },
      actions_performed: Array.isArray(log.actions_performed)
        ? log.actions_performed
        : [],
      temp_and_conditions: log.temp_and_conditions || '',
      attachments: log.attachments || [],
      comments: log.comments || '',
      completed_work: log.completed_work || null,
      property_details: {
        property_id:
          log.property_details?.property_id?._id ||
          log.property_details?.property_id ||
          '',
        building_name: log.property_details?.building_name || '',
      },
    };
  } catch (error) {
    console.error('Failed to fetch snow log for editing:', error);
    return defaultSnowLog;
  }
};